import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AIHowItWorks = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "How to Summarize Audio with Our AI Audio Summarizer",
      steps: [
        {
          icon: "fas fa-upload",
          title: "Upload Your Audio File",
          description: (
            <>
              Simply <strong>upload your audio file</strong> to our platform. You can <strong>convert audio to text</strong> from any source - recordings, podcasts, meetings, or lectures. <strong>Our AI audio summarizer</strong> supports all major audio formats and handles <strong>long recordings</strong> effortlessly.
            </>
          )
        },
        {
          icon: "fas fa-cogs",
          title: "Let AI Summarize Audio Content", 
          description: (
            <>
              <strong>Our audio summarizer</strong> uses <strong>advanced AI technology</strong> to analyze <strong>the audio</strong> and extract <strong>key points from</strong> your content. The AI identifies <strong>key insights</strong> and generates a comprehensive <strong>summary of</strong> your audio material automatically.
            </>
          )
        },
        {
          icon: "fas fa-file-text",
          title: "Get Your Summary",
          description: (
            <>
              Receive <strong>the summary</strong> with <strong>the key points</strong> highlighted. <strong>You can</strong> download, edit, or share <strong>your summary</strong> instantly. Convert <strong>audio into</strong> clear, actionable insights that save you hours <strong>of</strong> listening time.
            </>
          )
        }
      ]
    },
    ar: {
      title: "كيفية تلخيص الصوت باستخدام أداة التلخيص بالذكاء الاصطناعي",
      steps: [
        {
          icon: "fas fa-upload",
          title: "تحميل الملف الصوتي",
          description: (
            <>
              ببساطة <strong>قم بتحميل ملفك الصوتي</strong> إلى منصتنا. يمكنك <strong>تحويل الصوت إلى نص</strong> من أي مصدر - التسجيلات والبودكاست والاجتماعات أو المحاضرات. <strong>أداة التلخيص بالذكاء الاصطناعي</strong> تدعم جميع التنسيقات الصوتية الرئيسية وتتعامل مع <strong>التسجيلات الطويلة</strong> بسهولة.
            </>
          )
        },
        {
          icon: "fas fa-cogs",
          title: "دع الذكاء الاصطناعي يلخص المحتوى الصوتي",
          description: (
            <>
              <strong>أداة التلخيص الصوتي</strong> تستخدم <strong>تقنية الذكاء الاصطناعي المتقدمة</strong> لتحليل <strong>الصوت</strong> واستخراج <strong>النقاط الرئيسية من</strong> محتواك. يحدد الذكاء الاصطناعي <strong>الأفكار الرئيسية</strong> ويولد <strong>ملخصاً شاملاً</strong> لموادك الصوتية تلقائياً.
            </>
          )
        },
        {
          icon: "fas fa-file-text",
          title: "احصل على ملخصك",
          description: (
            <>
              احصل على <strong>الملخص</strong> مع تمييز <strong>النقاط الرئيسية</strong>. <strong>يمكنك</strong> تنزيل أو تعديل أو مشاركة <strong>ملخصك</strong> فوراً. حول <strong>الصوت إلى</strong> رؤى واضحة وقابلة للتنفيذ توفر لك ساعات من وقت الاستماع.
            </>
          )
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <div className="container-fluid mb-5" style={{ 
      fontFamily: '"Nunito Sans", "Montserrat", "Trebuchet MS", sans-serif',
      padding: '80px 0'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <h2 className="text-center fw-bold mb-4" style={{ 
              fontSize: '2.25rem', 
              fontWeight: '700', 
              color: '#ffffff',
              fontFamily: '"Roboto", "Poppins", sans-serif',
              paddingBottom: '20px'
            }}
            data-aos="fade-down"
            >
              {currentContent.title}
            </h2>
            
            {/* Decorative Divider */}
            <div className="d-flex justify-content-center align-items-center" style={{ marginTop: '40px', marginBottom: '120px' }} data-aos="fade-up">
              <div style={{ height: '2px', width: '60px', background: 'linear-gradient(90deg, transparent, #0D6EFD, transparent)' }}></div>
              <div className="mx-3" style={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%',
                background: '#0695FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(6, 149, 255, 0.4)'
              }}>
                <i className="fas fa-magic" style={{ color: 'white', fontSize: '1.25rem' }}></i>
              </div>
              <div style={{ height: '2px', width: '60px', background: 'linear-gradient(90deg, transparent, #0695FF, transparent)' }}></div>
            </div>
            
            <div className="row justify-content-center">
              <div className="col-lg-8 col-xl-6">
            {currentContent.steps.map((step, index) => (
              <div 
                key={index}
                data-aos="fade-down"
                data-aos-delay={index * 200}
              >
                <div className="d-flex align-items-start mb-4">
                  <div className="flex-shrink-0 me-4">
                    <div className="d-flex align-items-center justify-content-center rounded-circle text-white" style={{width: '60px', height: '60px', background: '#0695FF'}}>
                      <i className={`${step.icon} fa-lg`}></i>
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="h5 mb-3 fw-bold" style={{ 
                      color: '#ffffff', 
                      fontSize: '1.25rem', 
                      fontWeight: '600',
                      fontFamily: '"Roboto", "Poppins", sans-serif' 
                    }}>
                      {step.title}
                    </h4>
                    <p className="mb-0" style={{ 
                      color: '#e2e8f0', 
                      lineHeight: '1.6', 
                      fontSize: '0.95rem',
                      fontFamily: '"Tinos", "Times New Roman", serif' 
                    }}>
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < currentContent.steps.length - 1 && (
                  <div className="d-flex justify-content-center mb-4">
                    <div className="text-primary" style={{fontSize: '2rem'}}>
                      <i className="fas fa-arrow-down"></i>
                    </div>
                  </div>
                )}
              </div>
            ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIHowItWorks;